import type { CalculationEngine } from '../../core/applicationWorkflow.js';
import type { ValidatedBirthInput } from '../../core/birthInput.js';
import type { NormalizedChart, NormalizedHoroscope } from '../../core/domain.js';
import type { WorkerOperation, WorkerRequest, WorkerResponse } from './workerProtocol.js';

interface PendingRequest {
  resolve(value: unknown): void;
  reject(error: Error): void;
}

export class WorkerCalculationEngine implements CalculationEngine {
  readonly #worker: Worker;
  readonly #pending = new Map<number, PendingRequest>();
  #nextId = 1;

  constructor() {
    this.#worker = new Worker(new URL('./iztro.worker.ts', import.meta.url), { type: 'module', name: 'ziwei-iztro-v2.6.0' });
    this.#worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => {
      const response = event.data;
      const pending = this.#pending.get(response.id);
      if (!pending) return;
      this.#pending.delete(response.id);
      if (response.ok) pending.resolve(response.result);
      else pending.reject(new Error(response.error));
    });
    this.#worker.addEventListener('error', (event) => {
      const error = new Error(event.message || 'Iztro Web Worker failed');
      for (const pending of this.#pending.values()) pending.reject(error);
      this.#pending.clear();
    });
  }

  calculateNatal(input: ValidatedBirthInput): Promise<NormalizedChart> {
    return this.#request('calculateNatal', input) as Promise<NormalizedChart>;
  }

  calculateHoroscope(input: ValidatedBirthInput, targetDate: string, targetTimeIndex: number): Promise<NormalizedHoroscope> {
    return this.#request('calculateHoroscope', input, targetDate, targetTimeIndex) as Promise<NormalizedHoroscope>;
  }

  calculateNatalAndHoroscope(input: ValidatedBirthInput, targetDate: string, targetTimeIndex: number): Promise<{ chart: NormalizedChart; horoscope: NormalizedHoroscope }> {
    return this.#request('calculateNatalAndHoroscope', input, targetDate, targetTimeIndex) as Promise<{ chart: NormalizedChart; horoscope: NormalizedHoroscope }>;
  }

  dispose(): void {
    this.#worker.terminate();
    for (const pending of this.#pending.values()) pending.reject(new Error('Calculation engine disposed'));
    this.#pending.clear();
  }

  #request(operation: WorkerOperation, input: ValidatedBirthInput, targetDate?: string, targetTimeIndex?: number): Promise<unknown> {
    const id = this.#nextId++;
    const request: WorkerRequest = {
      id,
      operation,
      input,
      ...(targetDate !== undefined ? { targetDate } : {}),
      ...(targetTimeIndex !== undefined ? { targetTimeIndex } : {}),
    };
    return new Promise((resolve, reject) => {
      this.#pending.set(id, { resolve, reject });
      this.#worker.postMessage(request);
    });
  }
}
